import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../Button/Button';
import FlatButton from '../../FlatButton/FlatButton';
import {Tooltip} from 'react-tooltip';
import GoogleMarkerIcon from '../../../../components/Icons/GoogleMarkerIcon';
import InputAddress from '../../InputAddress/InputAddress';
import Input from '../../Input/Input';
import StyledCheckbox from '../../Checkbox/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import markerSrc from '../../../../assets/marker.png';
import { getCountries } from '../../../../store/organizationList/actions';
import config from 'config';

import classes from './createAddress.module.scss';

import {
  settingCompanySelector
} from '../../../../store/settings/selectors';
import { countriesSelector } from '../../../../store/organizationList/selectors';

import {
  setKey,
  fromAddress,
  fromLatLng,
} from "react-geocode";
import { withGoogleMap, GoogleMap, withScriptjs, Marker, Circle } from "react-google-maps";
import useGoogle from "react-google-autocomplete/lib/usePlacesAutocompleteService";

const initialFormValues = {
  address: '',
  coordinates: '',
  radius: '',
  disable_rtt: false,
};

const defaultProps = {
  googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${config.google.key}&v=3.exp&libraries=geometry,drawing,places`,
}

const AddressMap = withScriptjs(withGoogleMap(({
  mapRef,
  initData,
  currentDataRef,
  onMapClick,
  children,
}) => {
  const hasCurrentCenter = typeof currentDataRef.current.lat === 'number' && typeof currentDataRef.current.lng === 'number';
  const center = hasCurrentCenter
    ? { lat: currentDataRef.current.lat, lng: currentDataRef.current.lng }
    : { lat: initData.center_lat, lng: initData.center_lng };
  const zoom = typeof currentDataRef.current.zoom === 'number' ? currentDataRef.current.zoom : initData.zoom;

  return (
    <GoogleMap
      ref={mapRef}
      defaultZoom={initData.zoom}
      zoom={zoom}
      center={center}
      onClick={onMapClick}
      onZoomChanged={() => {
        if (mapRef.current) {
          currentDataRef.current.zoom = mapRef.current.getZoom();
        }
      }}
      onDragEnd={() => {
        if (mapRef.current) {
          currentDataRef.current.lat = mapRef.current.getCenter().lat();
          currentDataRef.current.lng = mapRef.current.getCenter().lng();
        }
      }}
    >
      {children}
    </GoogleMap>
  );
}));

export default function CreateAddress({
  handleClose, title, open,
  buttonTitle, createAddress, initialValues
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState(initialFormValues);
  const [dragMarker, setDragMarker] = useState(false);
  //const [currentZoom, setCurrentZoom] = useState(false);
  const [initData, setInitData] = useState({lat: 0, lng: 0, center_lat: 0, center_lng: 0, zoom: 3});
  const countries = useSelector(countriesSelector);
  const company = useSelector(settingCompanySelector);
  const mapRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const currentDataRef = useRef({
    zoom: null,
    lat: null,
    lng: null,
  });

  const {
    placePredictions,
    getPlacePredictions,
    //isPlacePredictionsLoading,
  } = useGoogle({
    apiKey: config.google.key,
  });

  useEffect(() => {
    setKey(config.google.key);
  }, []);

  useEffect(() => {
    dispatch(getCountries());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);
  
  const handleCreateAddress = () => {
    createAddress(formValues)
    onClose();
  };
  const onClose = () => {
    setFormValues({...initialFormValues});
    handleClose();

  };
  const setCurrentZoom = (zoom) => {
    currentDataRef.current.zoom = zoom;
    if (mapRef?.current?.setZoom) {
      mapRef.current.setZoom(zoom);
    }
  };

  const currentCoordinates = () => {
    if (!formValues.coordinates) return false;
    const coords = formValues.coordinates.split(',');
    return { lat: parseFloat(coords[0]), lng: parseFloat(coords[1]) };
  }

  const onMapClick = (event) => {
    if (!dragMarker) return;
    const { latLng } = event;
    const lat = latLng.lat();
    const lng = latLng.lng();
    
    setDragMarker(false);
    getAddressFromMarker(lat, lng);
  };

  const onMarkerDragEnd = (event) => {
    let lat = event.latLng.lat(),
        lng = event.latLng.lng();

    setDragMarker(false);
    getAddressFromMarker(lat, lng);
  };

  const getAddressFromMarker = (lat, lng) => {
    fromLatLng(lat, lng).then(
        response => {
            const address = response.results[0].formatted_address;
            setFormValues((prevState) => ({ ...prevState, address, coordinates: `${lat},${lng}` }));
        },
        error => {
          console.error(error);
        }
    );
  };

  const onFit = () => {
    fetchCoordinates(formValues.address, true);
  };

  const onPoint = () => {
    fetchCoordinates(formValues.address);
    setCurrentZoom(17);
  };

  const onSelectAddress = (address) => {
    fetchCoordinates(address, true);
    getPlacePredictions({ input: '' });
    setFormValues((prevState) => ({ ...prevState, address }));
  };

  const onClear = () => {
    getPlacePredictions({ input: '' });
    setFormValues((prevState) => ({ ...prevState, address: '', coordinates: '', radius: '' }));
    setCurrentZoom(7);
  };

  const fetchCoordinates = async (addressValue, setZoom) => {
    const addressToGeocode = (addressValue || '').trim();
    if (!addressToGeocode) return;

    try {
      const response = await fromAddress(addressToGeocode);
      const { lat, lng } = response.results[0].geometry.location;
      const coordinates = `${lat},${lng}`;
      if (setZoom) {
        const zoom = getBoundsZoomLevel(response.results[0].geometry.bounds, { height: 520, width: 1040 });
        setCurrentZoom(zoom);
      }

      setFormValues((prevState) => ({ ...prevState, address: addressToGeocode, coordinates }));
      setInitData((prevState) => ({ ...prevState, center_lat: lat, center_lng: lng }));
    } catch (error) {
      console.error('Error fetching coordinates:', error);
    }
  };

  const getBoundsZoomLevel = (bounds, mapDim) => {
    const WORLD_DIM = { height: 520, width: 1000 };
    const ZOOM_MAX = 21;

    const latRad = (lat) => {
        const sin = Math.sin(lat * Math.PI / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };

    const zoom = (mapPx, worldPx, fraction) => {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    };

    if (!bounds?.northeast || !bounds?.southwest) return 17;

    const ne = bounds.northeast;
    const sw = bounds.southwest;

    const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;

    const lngDiff = ne.lng - sw.lng;
    const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
};


  const searchAddress = () => {
    const query = (formValues.address || '').trim();
    if (!query) {
      getPlacePredictions({ input: '' });
      return;
    }
    getPlacePredictions({ input: query });
  };

  const onAddressChange = (event) => {
    const nextAddress = event.target.value;
    setFormValues((prevState) => ({ ...prevState, address: nextAddress }));

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const query = nextAddress.trim();
    if (query.length < 3) {
      getPlacePredictions({ input: '' });
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      getPlacePredictions({ input: query });
    }, 300);
  };


  const initCoords = async (address, setZoom) => {
    try {
      currentDataRef.current.lat = null;
      currentDataRef.current.lng = null;
      currentDataRef.current.zoom = null;
      const response = await fromAddress(address);
      const {lat, lng} = response.results[0].geometry.location;
      if (initialValues.coordinates) {
        setInitData((prevState) => ({
          ...prevState,
          lat: parseFloat(initialValues.coordinates.split(',')[0]),
          lng: parseFloat(initialValues.coordinates.split(',')[1]),
          center_lat: parseFloat(initialValues.coordinates.split(',')[0]),
          center_lng: parseFloat(initialValues.coordinates.split(',')[1]),
          zoom: 17,
        }));
      } else {
        setInitData({ lat, lng, center_lat: lat, center_lng: lng, zoom: 7 });
      }

      if (setZoom && !initialValues.coordinates) {
        const zoom = getBoundsZoomLevel(response.results[0].geometry.bounds, { height: 520, width: 1040 });
        setCurrentZoom(zoom);  
      } else if (initialValues.coordinates) {
        setCurrentZoom(17);
      } else {
        setCurrentZoom(7);
      }
    } catch (error) {
      console.error('Error fetching coordinates:', error);
    }
  };
  
  useEffect(() => {
    
    if (company && countries.length && open && !initialValues.address && initialValues?.id !== formValues?.id) {
      const foundCountry = countries.find(({ code }) => code === company.country);
      if (foundCountry) {
        initCoords(foundCountry?.name);
      }
    } else if (initialValues.address) {
      initCoords(initialValues.address, true);
      
    }

    if (initialValues) {
      setFormValues({
        address: initialValues.address,
        coordinates: initialValues.coordinates,
        radius: initialValues.radius,
        disable_rtt: initialValues.disable_rtt,
      });
    } else {
      setFormValues({
        ...initialFormValues,
      });
    }
// eslint-disable-next-line
  }, [initialValues, open]);
  
  return (
    <Dialog onClose={onClose} open={open} PaperProps={{
      style: {
        overflow: 'visible',
        width: '1040px',
        maxWidth: '1040vw',
      },
    }}>
      <div className={classes.createAddress}>
        <div className={classes.createAddress__title}>
          {title}
          <CloseIcon onClick={onClose} />
        </div>
        <div className={classes.createAddress__form}>
          <div className={classes.createAddress__head}>
            <div className={classes.createAddress__head_left}>
              <div className={classes.createAddress__head_inputs}>
                <InputAddress
                  placeholder={t('Enter address')}
                  value={formValues.address}
                  onSearch={searchAddress}
                  onCancel={() => getPlacePredictions({ input: '' })}
                  onSelect={onSelectAddress}
                  onChange={onAddressChange}
                  onClear={onClear}
                  onClickItem={(name) => { setFormValues((prevState) => ({ ...prevState, address: name })); }}
                  onFit={onFit}
                  onPoint={onPoint}
                  places={placePredictions}
                  width={'450px'}
                />
                <div data-tooltip-html={t('Pick location')} data-tooltip-id='google_marker' className={classes.createAddress__btnMarkerWrap}>
                  <FlatButton  onClick={() => setDragMarker(!dragMarker)} className={classes.createAddress__btnMarker}>
                    <GoogleMarkerIcon active={dragMarker} />
                  </FlatButton>
                </div>
                <div className={classes.createAddress__radiusrWrap}>
                  <Input
                    placeholder={t('Enter radius')}
                    value={formValues.radius}
                    width='130px'
                    onChange={(e) => setFormValues({ ...formValues, radius: e.target.value })}
                    onBlur={(e) => setFormValues({ ...formValues, radius: e.target.value < 100 ? 100 : e.target.value })}
                  />
                  { formValues.radius && <span>m</span> }
                </div>
                <div className={classes.tooltipBlock} data-tooltip-html={t('100 meters diameter is the minimum acceptable value by the system because of possible GPS discrepancy')} data-tooltip-id='google_marker'>
                  ?
                </div>
                { formValues.radius &&
                  <div className={classes.createAddress__head_inputs_area}>
                    {`${t('Area:')}`} <b>{`${Math.round(formValues.radius * formValues.radius * Math.PI)}`} m<sup>2</sup></b>
                  </div>
                }
              </div>
              <div className={classes.createAddress__checkboxWrap}>
                <StyledCheckbox
                  label={t('Don’t use real time tracking for this place')}
                  checked={formValues.disable_rtt}
                  onChange={(e) => setFormValues({ ...formValues, disable_rtt: !formValues.disable_rtt })}
                />
              </div>
            </div>
            <Button onClick={handleCreateAddress} size='likeinp'>
              {buttonTitle}
            </Button>
          </div>
          <div className={classes.createAddress__map}>
            <AddressMap
                googleMapURL={defaultProps.googleMapURL}
                mapRef={mapRef}
                initData={initData}
                currentDataRef={currentDataRef}
                onMapClick={onMapClick}
                loadingElement={<div style={{ height: `100%` }} />}
                containerElement={<div style={{ height: `520px` }} />}
                mapElement={<div style={{ height: `100%` }} />}
            >
              {
                formValues.coordinates && currentCoordinates() &&
                <Marker
                    icon={markerSrc}
                    draggable={dragMarker}
                    onDragEnd={onMarkerDragEnd}
                    position={currentCoordinates()}
                >
                  {formValues.radius &&
                    <Circle
                      defaultCenter={currentCoordinates()}
                      radius={formValues.radius*1}
                      options={{strokeColor: "#996AFF", strokeWeight: 1, fillColor: "#996AFF33"}}
                    />
                  }
                </Marker>
              }
            </AddressMap>
            
          </div>
        </div>
        <Tooltip
          id='google_marker'
          className='schedule-screen__tooltip__black'
          effect='solid'
          placement='bottom'
        />
        <Tooltip
          id='google_marker_button'
          className='schedule-screen__tooltip__black'
          effect='solid'
          placement='bottom'
        />
      </div>
    </Dialog>
  );
}